import { useState } from 'react';
import { useNavigate } from 'react-router';
import { MoreVertical, RefreshCw, Send, Trash2 } from 'lucide-react';
import { Button } from '../../ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../ui/dropdown-menu';
import { ConfirmationDialog } from '../../ui/ConfirmationDialog';
import { SendEmailsDialog } from '../SendEmailsDialog';
import {
  useDeleteDrawdown,
  useRegenerateAllDrawdownPDFs,
  useSendDrawdownEmails,
} from '@/hooks/react-query/useDrawdowns';
import type { DrawdownGroup } from '@/types/drawdown';

interface DrawdownActionsMenuProps {
  fundId: string;
  drawdownId: string;
  drawdownName: string;
  /** True while the server-side queue is generating documents for any batch. */
  docsGenerating?: boolean;
  /** Drawdown shaped for the shared Send-Emails dialog; null hides the action. */
  sendEmailsGroup?: DrawdownGroup | null;
  /** Emails can only go out once the drawdown has been issued to investors. */
  canSendEmails?: boolean;
  /** Where to navigate once the drawdown has been deleted. */
  backTo?: string;
}

export function DrawdownActionsMenu({
  fundId,
  drawdownId,
  drawdownName,
  docsGenerating = false,
  sendEmailsGroup = null,
  canSendEmails = false,
  backTo,
}: DrawdownActionsMenuProps) {
  const navigate = useNavigate();
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showEmailDialog, setShowEmailDialog] = useState(false);

  const regenerateAllPDFsMutation = useRegenerateAllDrawdownPDFs();
  const deleteDrawdownMutation = useDeleteDrawdown();
  const sendDrawdownEmailsMutation = useSendDrawdownEmails();

  const isRegenerating = regenerateAllPDFsMutation.isPending || docsGenerating;

  const handleRegenerateAllPDFs = async () => {
    if (isRegenerating) return;
    try {
      await regenerateAllPDFsMutation.mutateAsync({ drawdownId, fundId });
    } catch (error) {
      console.error('Failed to regenerate PDFs:', error);
    }
  };

  const handleSendEmails = async (newOnly: boolean) => {
    try {
      await sendDrawdownEmailsMutation.mutateAsync({
        fundId,
        drawdownId,
        newOnly,
      });
      setShowEmailDialog(false);
    } catch (error) {
      console.error('Failed to send emails:', error);
    }
  };

  const handleDeleteDrawdown = async () => {
    try {
      await deleteDrawdownMutation.mutateAsync({ drawdownId, fundId });
      setShowDeleteDialog(false);
      if (backTo) {
        navigate(backTo);
      }
    } catch (error) {
      console.error('Failed to delete drawdown:', error);
    }
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="sm" aria-label="Drawdown actions">
            <MoreVertical className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem
            onClick={handleRegenerateAllPDFs}
            disabled={isRegenerating}
          >
            <RefreshCw
              className={`h-4 w-4 mr-2 ${isRegenerating ? 'animate-spin' : ''}`}
            />
            {isRegenerating
              ? 'Generating documents...'
              : 'Refresh Notification PDFs'}
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={() => setShowEmailDialog(true)}
            disabled={!canSendEmails || isRegenerating}
          >
            <Send className="h-4 w-4 mr-2" />
            Send Emails to Investors
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            onClick={() => setShowDeleteDialog(true)}
            className="text-red-600 focus:text-red-600 focus:bg-red-50"
          >
            <Trash2 className="h-4 w-4 mr-2" />
            Delete Drawdown
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      {sendEmailsGroup && (
        <SendEmailsDialog
          open={showEmailDialog}
          onClose={() => setShowEmailDialog(false)}
          drawdown={sendEmailsGroup}
          onConfirm={handleSendEmails}
          isLoading={sendDrawdownEmailsMutation.isPending}
        />
      )}

      <ConfirmationDialog
        open={showDeleteDialog}
        onOpenChange={setShowDeleteDialog}
        title="Delete Drawdown"
        description={`Are you sure you want to delete "${drawdownName}"? This will permanently remove the drawdown and all associated data including investor notifications, documents, email records, and ledger entries. This action cannot be undone.`}
        confirmLabel="Delete"
        cancelLabel="Cancel"
        variant="destructive"
        onConfirm={handleDeleteDrawdown}
        isLoading={deleteDrawdownMutation.isPending}
      />
    </>
  );
}
